import config from '../config/server-config';
import { API_CALL_SUCCESSFULL } from '../constants/NumberConstants';
import { history } from './navigation-util';
import api, { cancelProactiveRefresh, scheduleProactiveRefresh } from '../api';
import { clearTokenCache, refreshUserInfoFromToken } from './token-util';

export const authenticate = async (user, password, costCode) => {
  try {
    const response = await api.post(
      config.userAuthenticate,
      {
        username: user,
        password,
        costCode,
      },
      { validateStatus: (status) => status < 500 },
    );
    if (response.status === API_CALL_SUCCESSFULL) {
      if (response.data.token) {
        window.localStorage.setItem('token', response.data.token);
        window.localStorage.setItem('userEmail', user);
        localStorage.removeItem('userInfo');
        clearTokenCache();
        await refreshUserInfoFromToken(response.data.token);
        scheduleProactiveRefresh(response.data.token);

        return {
          token: response.data.token,
          text: response.data.text,
          userid: response.data.User_Id,
          userEmail: user,
          isValid: true,
          foreignSecurityKey: response.data.foreignSecurityKey,
          showActivistIcon: response.data.showActivistIcon,
          showProxyIcon: response.data.showProxyIcon,
        };
      }
      if (response.data.ispayguser) {
        return response.data;
      }
      return { isValid: false, message: response.data.text, loginErrorType: response.data.loginErrorType };
    }

    return {
      isValid: false,
      message: response.data.text,
      loginErrorType: response.data.loginErrorType,
    };
  } catch (e) {
    const parsedMessage = e?.response?.data?.text;
    if (parsedMessage) {
      return {
        isValid: false,
        message: parsedMessage,
        loginErrorType: e?.response?.data?.loginErrorType,
      };
    }
    return false;
  }
};

export const authenticateViaHighbond = async () => {
  try {
    const response = await api.post(config.userAuthenticateHighBond);
    if (response.status === API_CALL_SUCCESSFULL) {
      if (response.data.token) {
        window.localStorage.setItem('token', response.data.token);
        window.localStorage.setItem('userEmail', response.data.userName);
        localStorage.removeItem('userInfo');
        clearTokenCache();
        await refreshUserInfoFromToken(response.data.token);
        scheduleProactiveRefresh(response.data.token);

        return {
          status: response.status,
          token: response.data.token,
          text: response.data.text,
          userid: response.data.User_Id,
          userEmail: response.data.userName,
          isValid: true,
          foreignSecurityKey: response.data.foreignSecurityKey,
          showActivistIcon: response.data.showActivistIcon,
          showProxyIcon: response.data.showProxyIcon,
        };
      }
      return { isValid: false, status: response.status, message: response.data.text };
    }
    return { isValid: false, status: response.status, message: response.data.text };
  } catch (e) {
    return false;
  }
};

export const isUserAuthenticated = () => {
  if (window.sessionStorage.getItem('lastPage') && window.localStorage.getItem('token') === null) {
    window.sessionStorage.setItem('redirect', window.sessionStorage.getItem('lastPage'));
    window.sessionStorage.removeItem('lastPage');
  }
  return window.localStorage.getItem('token');
};

export const isUserAuthenticatedBoolean = () => window.localStorage.getItem('token') !== null;

export const sendForgotPasswordEmail = async (email) => {
  try {
    const data = {
      email,
      hostName: window.location.host,
    };

    const response = await api.post(config.sendForgotPasswordEmail, data);
    if (response.status === API_CALL_SUCCESSFULL) {
      return true;
    }
  } catch (error) {
    return false;
  }
};

export const resetNewPassword = async (password, resetcode, email) => {
  try {
    const data = {
      pwdresettoken: resetcode,
      newpassword: password,
      email,
    };

    const response = await api.post(config.resetPassword, data, {
      skipAuthRefresh: true,
      skipAuthErrorHandling: true,
    });
    if (response.status === API_CALL_SUCCESSFULL) {
      return response.data;
    }
  } catch (e) {
    const data = e.response?.data;
    // Server uses 400 for validation/missing params and 500 for unexpected errors; both may include text (see authRoutes resetPassword).
    if (data && (data.text || data.errors)) {
      const text = data.text || (Array.isArray(data.errors) && data.errors.length ? data.errors[0] : 'Request failed');
      return { text, output: { ResetStatus: -1 } };
    }
    return false;
  }
};

export const logout = async (userid, sessionid) => {
  cancelProactiveRefresh();
  clearTokenCache();

  const accessToken = window.localStorage.getItem('token');
  try {
    await api.post(config.logout, { userid, sessionid, accessToken });
  } finally {
    localStorage.removeItem('token');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('notificationData');
    localStorage.removeItem('alertNotificationData');
    localStorage.removeItem('investorFilterData');
    localStorage.removeItem('companyFilterData');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('persist:root');
  }
};

export const lookupDecIPForIPLogin = async () => {
  try {
    const response = await api.post(config.lookupDecIPForIPLogin);
    if (response.status === API_CALL_SUCCESSFULL) {
      return response.data;
    }
  } catch (e) {
    return false;
  }
};

export const authenticateIPLogin = async () => {
  try {
    const response = await api.post(config.authenticateIPLogin);
    if (response.status === API_CALL_SUCCESSFULL) {
      if (response.data.token) {
        window.localStorage.setItem('token', response.data.token);
        window.localStorage.setItem('userEmail', response.data.UserEmail);
        localStorage.removeItem('userInfo');
        clearTokenCache();
        await refreshUserInfoFromToken(response.data.token);
        scheduleProactiveRefresh(response.data.token);
        return {
          token: response.data.token,
          text: response.data.text,
          userEmail: response.data.UserEmail,
          userid: response.data.User_Id,
          isValid: true,
        };
      }
      return { isValid: false, message: response.data.text };
    }
  } catch (e) {
    return false;
  }
};

export const createComputer = async (computer_code, owner, user_agent, browser, version) => {
  try {
    const response = await api.post(config.createComputer, {
      computer_code,
      owner,
      user_agent,
      browser,
      version,
    });
    if (response.status === API_CALL_SUCCESSFULL) {
      return response.data;
    }
  } catch (e) {
    return false;
  }
};

export const UpdateComputerLogin = async (computer_id) => {
  try {
    const response = await api.post(config.updateComputerLogin, {
      computer_id,
    });
    if (response.status === API_CALL_SUCCESSFULL) {
      return response.data;
    }
  } catch (e) {
    return false;
  }
};

export const IsBranchUpToDate = async (res) => {
  try {
    const response = await api.post(config.isBranchUpToDate, { data: res });
    if (response.status === API_CALL_SUCCESSFULL) {
      return response.data;
    }
    return [];
  } catch (e) {
    return false;
  }
};
export const LoginViaAdmin = async (otk) => {
  try {
    const response = await api.post(config.loginViaAdmin, {
      otk,
    });
    if (response.status === API_CALL_SUCCESSFULL) {
      if (response.data.token) {
        window.localStorage.setItem('token', response.data.token);
        window.localStorage.setItem('userEmail', response.data.email);
        localStorage.removeItem('userInfo');
        clearTokenCache();
        await refreshUserInfoFromToken(response.data.token);
        scheduleProactiveRefresh(response.data.token);
        history.push('/');
        return {
          token: response.data.token,
          text: response.data.text,
          userid: response.data.User_Id,
          userEmail: response.data.email,
          isValid: true,
        };
      }
      history.push('/credential-form');
      return { isValid: false, message: response.data.text };
    }
  } catch (e) {
    return false;
  }
};
